import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Container from "@/components/Container";
import Loading from "@/components/Loading";
import Calendar from "@/components/Calendar";
import { Input, Select } from "@/components/UI";
import { Button } from "@/components/UI/Buttons/Button";
import SuccessPage from "@/components/SuccessPage";
import rezusRegistrationApi, {
  type RezusLocation,
} from "@/services/rezusRegistrationApi";
import { AppointmentData, TimeSlot } from "@/data/interfaces/Appointments";
import ROUTES from "@/data/Routes";

const RezusRegistration: FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [locations, setLocations] = useState<RezusLocation[]>([]);
  const [selectedLocation, setSelectedLocation] =
    useState<RezusLocation | null>(null);
  const [appointments, setAppointments] = useState<AppointmentData[]>([]);
  const [selectedAppointment, setSelectedAppointment] =
    useState<AppointmentData | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<TimeSlot | null>(null);
  const [consent, setConsent] = useState(false);

  const [loadingLocations, setLoadingLocations] = useState(true);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError("Neteisinga nuoroda");
      setLoadingLocations(false);
      return;
    }
    (async () => {
      try {
        const data = await rezusRegistrationApi.getLocations(token);
        setLocations(Array.isArray(data) ? data : []);
      } catch {
        setError("Nepavyko įkelti lokacijų sąrašo");
      } finally {
        setLoadingLocations(false);
      }
    })();
  }, [token]);

  useEffect(() => {
    if (!token || !selectedLocation) return;
    setLoadingAppointments(true);
    setAppointments([]);
    setSelectedAppointment(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setError(null);
    (async () => {
      try {
        const data = await rezusRegistrationApi.getAppointments(
          token,
          selectedLocation.city,
        );
        setAppointments(data);
        if (data.length === 1) {
          setSelectedAppointment(data[0]);
        }
      } catch {
        setError("Nepavyko įkelti paslaugų");
      } finally {
        setLoadingAppointments(false);
      }
    })();
  }, [token, selectedLocation]);

  const handleDateTimeSelect = useCallback(
    (date: string, time?: TimeSlot) => {
      setSelectedDate(date);
      setSelectedTime(time || null);
    },
    [],
  );

  const fetchSlots = useCallback(
    (date: string) => {
      if (!token || !selectedAppointment) return Promise.resolve([]);
      return rezusRegistrationApi.getSlots(token, {
        location: selectedAppointment.connection.location,
        service: selectedAppointment.connection.service,
        worker: selectedAppointment.connection.worker,
        date,
      });
    },
    [token, selectedAppointment],
  );

  const summary = useMemo(() => {
    if (!selectedAppointment || !selectedDate || !selectedTime) return null;
    return {
      locationName: selectedAppointment.location?.name,
      serviceName: selectedAppointment.service?.name,
      date: selectedDate,
      time: selectedTime,
    };
  }, [selectedAppointment, selectedDate, selectedTime]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!selectedAppointment || !selectedDate || !selectedTime) {
      setSubmitError("Pasirinkite datą ir laiką");
      return;
    }
    if (!consent) {
      setSubmitError("Reikia sutikti su duomenų tvarkymu");
      return;
    }

    try {
      setIsSubmitting(true);
      await rezusRegistrationApi.registerAppointment(token!, {
        location: selectedAppointment.connection.location,
        location_name: selectedAppointment.location.name,
        service: selectedAppointment.connection.service,
        service_name: selectedAppointment.service.name,
        worker: selectedAppointment.connection.worker,
        worker_name: selectedAppointment.worker.name,
        date: selectedDate,
        start: selectedTime.value + ":00",
      });
      setIsSuccess(true);
    } catch (err: any) {
      setSubmitError(
        err?.message ||
          err?.data?.message ||
          "Nepavyko pateikti registracijos",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <SuccessPage
        title="Registracija sėkminga!"
        buttons={[
          {
            label: "Grįžti į pagrindinį puslapį",
            onClick: () => navigate(ROUTES.HOME),
          },
        ]}
      >
        <p className="text-muted text-lg">
          Jūsų vizitas sėkmingai užregistruotas.
        </p>
      </SuccessPage>
    );
  }

  const handleLocationChange = (locationId: string) => {
    setSelectedLocation(
      locations.find((l) => l.location.id === locationId) ?? null,
    );
  };

  const handleProductChange = (serviceId: string) => {
    const match = appointments.find(
      (a) => String(a.connection.service) === serviceId,
    );
    setSelectedAppointment(match ?? null);
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const uniqueLocations = locations.filter(
    (l, i, arr) =>
      arr.findIndex((x) => x.location.id === l.location.id) === i,
  );

  return (
    <div className="flex min-h-screen w-full flex-col items-center bg-gray-100 pt-25 md:pt-45">
      <Container className="px-4">
        <div className="mx-auto flex w-full max-w-[600px] flex-col items-center gap-6">
          <h1 className="text-dark text-center text-[30px] leading-[1.2] font-normal md:text-[44px]">
            Registracija mėginių pridavimui
          </h1>
          <p className="text-dark text-center text-[16px] leading-[1.4] md:text-[18px]">
            Pasirinkite lokaciją, paslaugą, datą ir laiką.
          </p>

          {loadingLocations && (
            <div className="flex min-h-[200px] items-center justify-center">
              <Loading size={48} label="Kraunama..." />
            </div>
          )}

          {error && <div className="text-sm text-red-600">{error}</div>}

          {!loadingLocations && !error && uniqueLocations.length === 0 && (
            <div className="text-muted text-sm">
              Šiuo metu registracija negalima. Bandykite vėliau.
            </div>
          )}

          {!loadingLocations && uniqueLocations.length > 0 && (
            <form
              className="flex w-full flex-col gap-[24px] pb-20"
              onSubmit={handleSubmit}
            >
              <Select
                label="Lokacija:"
                options={uniqueLocations.map((l) => ({
                  label: l.location.name,
                  value: l.location.id,
                }))}
                value={selectedLocation?.location.id}
                onValueChange={handleLocationChange}
                placeholder="Pasirinkite lokaciją..."
              />

              {loadingAppointments && (
                <div className="flex items-center justify-center py-8">
                  <Loading size={32} label="Kraunamos paslaugos..." />
                </div>
              )}

              {!loadingAppointments &&
                selectedLocation &&
                appointments.length === 0 && (
                  <div className="text-muted text-sm">
                    Šioje lokacijoje paslaugų nerasta.
                  </div>
                )}

              {appointments.length > 1 && (
                <Select
                  label="Paslauga:"
                  options={appointments.map((a) => ({
                    label: a.service.name,
                    value: String(a.connection.service),
                  }))}
                  value={
                    selectedAppointment
                      ? String(selectedAppointment.connection.service)
                      : undefined
                  }
                  onValueChange={handleProductChange}
                  placeholder="Pasirinkite paslaugą..."
                />
              )}

              {appointments.length === 1 && selectedAppointment && (
                <Input
                  label="Paslauga:"
                  type="text"
                  disabled
                  value={selectedAppointment.service?.name || ""}
                  name="service_name"
                />
              )}

              {selectedAppointment && (
                <Calendar
                  appointmentOptions={selectedAppointment}
                  onSelect={handleDateTimeSelect}
                  fetchSlots={fetchSlots}
                />
              )}

              {summary && (
                <>
                  <VisitSummary summary={summary} />

                  <Input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.currentTarget.checked)}
                    name="consent"
                    label="Paspausdami mygtuką Patvirtinti, Jūs sutinkate su duomenų saugojimu ir tvarkymu šioje svetainėje / By clicking Submit, you consent to the storage and processing of data at this website"
                  />

                  {submitError && (
                    <div className="text-sm text-red-600">{submitError}</div>
                  )}

                  <Button
                    type="submit"
                    variant="ink"
                    size="checkout"
                    disabled={isSubmitting}
                    className="mt-4"
                  >
                    {isSubmitting ? "Registruojama..." : "Patvirtinti"}
                  </Button>
                </>
              )}
            </form>
          )}
        </div>
      </Container>
    </div>
  );
};

const VisitSummary: FC<{
  summary: {
    locationName: string;
    serviceName: string;
    date: string;
    time: TimeSlot;
  };
}> = ({ summary }) => (
  <div className="bg-paper border-line mt-2 flex flex-col gap-3 rounded-2xl border p-4 text-base md:text-lg">
    <h3 className="text-ink text-xl font-semibold md:text-2xl">
      Vizito peržiūra
    </h3>
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2">
      <dt className="text-muted font-medium">Paslauga:</dt>
      <dd className="text-ink m-0">{summary.serviceName}</dd>
      <dt className="text-muted font-medium">Vieta:</dt>
      <dd className="text-ink m-0">{summary.locationName}</dd>
      <dt className="text-muted font-medium">Data:</dt>
      <dd className="text-ink m-0">{summary.date}</dd>
      <dt className="text-muted font-medium">Laikas:</dt>
      <dd className="text-ink m-0">{summary.time.value.slice(0, 5)}</dd>
    </dl>
  </div>
);

export default RezusRegistration;
